/**
 * Prompt chips (SPEC §6 item 8): three buttons that copy a ready-made prompt
 * for the agent to the clipboard, with a short "Copied" flash and a polite
 * live announcement.
 *
 *   const chips = mountPromptChips(container);
 *   chips.dispose();
 */
import { h } from './dom';

/** The three prompts, verbatim from the spec. */
export const PROMPT_CHIPS: readonly string[] = [
  'Add lo-fi drums',
  'What chords go under what I just played?',
  'Let me hear two bass options',
];

/** How long the "Copied" state stays on a chip, in ms. */
export const COPIED_FLASH_MS = 1500;

export interface PromptChipsHandle {
  /** Root element (`.bm-chips`). */
  el: HTMLElement;
  dispose(): void;
}

/** Copy `text` to the clipboard; resolves false when no clipboard is available or the write fails. */
export async function copyText(text: string): Promise<boolean> {
  try {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (clipboard && typeof clipboard.writeText === 'function') {
      await clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.append(ta);
    ta.select();
    const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
    ta.remove();
    return !!ok;
  } catch {
    return false;
  }
}

/** Mount the chips into `container`. */
export function mountPromptChips(container: HTMLElement, prompts: readonly string[] = PROMPT_CHIPS): PromptChipsHandle {
  const status = h('span', { class: 'bm-visually-hidden', role: 'status', 'aria-live': 'polite' });
  const timers = new Map<HTMLElement, number>();

  const buttons = prompts.map((text) => {
    const btn = h('button', { type: 'button', class: 'bm-chip', title: 'Copy this prompt for your agent' }, text);
    btn.addEventListener('click', async () => {
      const ok = await copyText(text);
      if (!btn.isConnected) return;
      const prev = timers.get(btn);
      if (prev) clearTimeout(prev);
      btn.setAttribute('data-copied', ok ? '' : 'failed');
      status.textContent = ok ? `Copied: ${text}` : 'Copy failed — select the text and copy it yourself';
      timers.set(
        btn,
        window.setTimeout(() => {
          btn.removeAttribute('data-copied');
          timers.delete(btn);
        }, COPIED_FLASH_MS),
      );
    });
    return btn;
  });

  const el = h('div', { class: 'bm-chips', role: 'group', 'aria-label': 'Prompts to copy for your agent' }, buttons, status);
  container.append(el);

  return {
    el,
    dispose() {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      el.remove();
    },
  };
}
